import React, { useState, useEffect, useCallback } from 'react';
import { personaSkillsAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import ConfirmModal from '../components/ConfirmModal';
import Toast from '../components/Toast';

const PERSONAS = ['immigration', 'incorporation', 'compliance', 'banking', 'concierge'];
const PERSONA_COLOR = {
  immigration: '#2563EB',
  incorporation: '#7C3AED',
  compliance: '#D97706',
  banking: '#0891B2',
  concierge: '#16A34A',
};

const EMPTY = { persona: 'immigration', skill_key: '', title: '', tags: '', content: '', is_active: 1 };

const PersonaSkills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [modal, setModal] = useState(null); // null | {mode:'create'|'edit', data}
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await personaSkillsAPI.getAll(filter ? { persona: filter } : {});
      setSkills(data.skills || []);
    } catch (e) {
      setToast({ type: 'error', message: 'Failed to load skills' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchSkills(); }, [fetchSkills]);

  const openCreate = () => { setForm(EMPTY); setModal({ mode: 'create' }); };
  const openEdit = (s) => {
    setForm({ persona: s.persona, skill_key: s.skill_key, title: s.title, tags: s.tags || '', content: s.content, is_active: s.is_active });
    setModal({ mode: 'edit', id: s.id });
  };

  const save = async () => {
    if (!form.skill_key.trim() || !form.title.trim() || !form.content.trim()) {
      setToast({ type: 'error', message: 'Key, title and content are required' });
      return;
    }
    try {
      setSaving(true);
      if (modal.mode === 'create') {
        await personaSkillsAPI.create(form);
        setToast({ type: 'success', message: 'Skill created' });
      } else {
        await personaSkillsAPI.update(modal.id, form);
        setToast({ type: 'success', message: 'Skill updated' });
      }
      setModal(null);
      fetchSkills();
    } catch (e) {
      setToast({ type: 'error', message: e?.response?.data?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await personaSkillsAPI.delete(deleteId);
      setToast({ type: 'success', message: 'Skill deleted' });
      setDeleteId(null);
      fetchSkills();
    } catch (e) {
      setToast({ type: 'error', message: 'Delete failed' });
    }
  };

  const inputStyle = { width: '100%', padding: '9px 11px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6, display: 'block' };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">AI PERSONA SKILLS</h1>
            <p className="page-subtitle">DOMAIN KNOWLEDGE THE ADVISOR USES (L2)</p>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...inputStyle, width: 'auto' }}>
              <option value="">All personas</option>
              {PERSONAS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button className="btn-primary" onClick={openCreate}>+ Add Skill</button>
          </div>
        </div>

        <div style={{ padding: '0 24px 40px' }}>
          {loading ? (
            <p style={{ color: '#6B7280' }}>Loading…</p>
          ) : skills.length === 0 ? (
            <p style={{ color: '#6B7280' }}>No skills yet. Add the first one.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {skills.map((s) => (
                <div key={s.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: PERSONA_COLOR[s.persona] || '#6B7280', padding: '2px 9px', borderRadius: 999, textTransform: 'uppercase' }}>{s.persona}</span>
                    <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#9CA3AF' }}>{s.skill_key}</span>
                    {!s.is_active && <span style={{ fontSize: 11, color: '#DC2626' }}>(inactive)</span>}
                    <div style={{ flex: 1 }} />
                    <button onClick={() => openEdit(s)} style={{ border: '1px solid #E5E7EB', background: '#F9FAFB', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Edit</button>
                    <button onClick={() => setDeleteId(s.id)} style={{ border: '1px solid #FEE2E2', background: '#FEF2F2', color: '#DC2626', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13 }}>Delete</button>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 4 }}>{s.title}</div>
                  <div style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{s.content}</div>
                  {s.tags && <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>tags: {s.tags}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setModal(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: 'min(640px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 18px' }}>{modal.mode === 'create' ? 'Add Skill' : 'Edit Skill'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Persona</label>
                <select value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })} style={inputStyle}>
                  {PERSONAS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Skill key</label>
                <input value={form.skill_key} onChange={(e) => setForm({ ...form, skill_key: e.target.value })} placeholder="e.g. o1-evidence-strategy" style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Title</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Content (the knowledge the advisor uses)</label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, marginBottom: 20, alignItems: 'end' }}>
              <div>
                <label style={labelStyle}>Tags (comma-separated)</label>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="visa, o1, evidence" style={inputStyle} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, paddingBottom: 8 }}>
                <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} /> Active
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setModal(null)} style={{ border: '1px solid #E5E7EB', background: '#fff', borderRadius: 8, padding: '9px 18px', cursor: 'pointer' }}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          title="Delete skill?"
          message="This removes the knowledge from the advisor. This cannot be undone."
          onConfirm={doDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </div>
  );
};

export default PersonaSkills;
